import {
  Badge,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { IconArrowLeftBar, IconMapPin } from "@tabler/icons-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { countryCityGroups } from "../data/cityPages";
import Seo from "../seo/Seo";
import {
  createBreadcrumbSchema,
  createOrganizationSchema,
} from "../seo/utils";
import "./Services/ServiceLandingPage.css";
import "./CityPage.css";

function CountryPage() {
  const theme = useMantineTheme();
  const { country } = useParams<{ country: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const countrySlug = country || location.pathname.split("/").filter(Boolean)[0];
  const countryGroup = countryCityGroups.find(
    (group) => group.countrySlug === countrySlug?.toLowerCase(),
  );

  if (!countryGroup) {
    return (
      <Container py="xl">
        <Stack gap="md">
          <Seo
            title="Country Page Not Found | SubjectBuddy"
            description="The country assignment help page you requested could not be found."
            path={location.pathname}
            noIndex
          />
          <Title order={2}>Country page not found</Title>
          <Button variant="subtle" onClick={() => navigate("/")}>
            Back to home
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container fluid mt={50} className="service-page">
      <Seo
        title={`${countryGroup.country} Assignment Help | SubjectBuddy`}
        description={`Explore SubjectBuddy assignment help across major education cities in ${countryGroup.country}.`}
        path={location.pathname}
        keywords={[
          `assignment help ${countryGroup.country.toLowerCase()}`,
          `${countryGroup.country.toLowerCase()} assignment help`,
          "online assignment help",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: countryGroup.country, path: `/${countryGroup.countrySlug}` },
          ]),
        ]}
      />

      <Container size="lg">
        <Stack gap="xl">
          <Button
            color="blue"
            variant="subtle"
            leftSection={<IconArrowLeftBar />}
            onClick={() => navigate("/")}
            pl={0}
            maw={140}
          >
            Back
          </Button>

          <Paper radius="xl" p="xl" className="service-hero-card city-hero">
            <Stack gap="lg">
              <Group gap="sm">
                <Badge color="green" size="lg" radius="sm">
                  {countryGroup.countryCode}
                </Badge>
                <Badge color="blue" size="lg" radius="sm" variant="light">
                  {countryGroup.cities.length} cities
                </Badge>
              </Group>
              <Title order={1} c={theme.colors.green[9]}>
                Assignment Help in {countryGroup.country}
              </Title>
              <Text c="dimmed" maw={760} size="lg">
                Choose your city to find assignment planning, tutoring, draft
                review, referencing, and subject support tailored for students in
                major education hubs.
              </Text>
            </Stack>
          </Paper>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {countryGroup.cities.map((city) => (
              <Paper
                key={city.path}
                component={Link}
                to={city.path}
                withBorder
                radius="lg"
                p="lg"
                className="city-country-card"
              >
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconMapPin size={18} color={theme.colors.green[7]} />
                    <Text fw={800} c={theme.colors.green[8]}>
                      {city.city}
                    </Text>
                  </Group>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                    {city.description}
                  </Text>
                  <Text size="sm" fw={700} c="blue">
                    View city support
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </Container>
  );
}

export default CountryPage;
